'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import {
  getPokerSiteLogoSlug,
  LOGO_CUSTOM_BG,
  LOGO_LOCAL_ONLY_SLUGS,
  LOGO_PNG_ONLY_SLUGS,
  POKER_SITE_LOGO_FALLBACK,
  POKER_SITE_LOGO_URL,
} from '@/lib/poker-sites';
import { usePokerSiteAdminLogo } from '@/hooks/usePokerSiteAdminLogo';

interface PokerSiteLogoProps {
  siteName: string;
  className?: string;
  size?: number;
}

export const PokerSiteLogo: React.FC<PokerSiteLogoProps> = ({
  siteName,
  className = '',
  size = 40,
}) => {
  const adminLogoUrl = usePokerSiteAdminLogo(siteName);
  const [adminFailed, setAdminFailed] = useState(false);

  const slug = getPokerSiteLogoSlug(siteName);
  const fallback = POKER_SITE_LOGO_FALLBACK[siteName];
  const logoUrl = POKER_SITE_LOGO_URL[siteName];

  const [urlLoaded, setUrlLoaded] = useState(false);
  const [urlFailed, setUrlFailed] = useState(false);
  const [localLoaded, setLocalLoaded] = useState(false);
  const [localFailed, setLocalFailed] = useState(false);
  const [localExt, setLocalExt] = useState<'svg' | 'png'>(() =>
    slug && LOGO_PNG_ONLY_SLUGS.has(slug) ? 'png' : 'svg'
  );

  if (adminLogoUrl && !adminFailed) {
    return (
      <div
        className={`rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 ${className}`}
        style={{ width: size, height: size, position: 'relative' as const }}
      >
        <Image
          src={adminLogoUrl}
          alt=""
          fill
          sizes={`${size}px`}
          className="object-contain p-1"
          unoptimized
          onError={() => setAdminFailed(true)}
        />
      </div>
    );
  }

  const bgColor = (slug && LOGO_CUSTOM_BG[slug]) ?? fallback?.bgColor ?? '#6b7280';
  const isLightBg = bgColor === '#ffffff' || bgColor === '#ffcc00';

  // Site connu : afficher fond + initiale, et charger le vrai logo (URL officielle ou fichier local)
  // Pour les sites en LOGO_PNG_ONLY_SLUGS, on n'utilise que le fichier local (pas le favicon)
  if (fallback) {
    const useOnlyLocalLogo = slug && (LOGO_PNG_ONLY_SLUGS.has(slug) || LOGO_LOCAL_ONLY_SLUGS.has(slug));
    const showUrlImage = !useOnlyLocalLogo && logoUrl && urlLoaded && !urlFailed;
    const showLocalImage = slug && localLoaded && !localFailed && !showUrlImage;
    const tryUrl = !useOnlyLocalLogo && logoUrl && !urlFailed;
    const tryLocal = slug && !localFailed;

    return (
      <div
        className={`rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 border ${className} ${
          isLightBg ? 'text-gray-900 border-gray-200 dark:border-gray-600' : 'text-white border-transparent'
        }`}
        style={{
          width: size,
          height: size,
          backgroundColor: bgColor,
          position: 'relative' as const,
        }}
      >
        {!useOnlyLocalLogo && (
          <span
            className="absolute inset-0 flex items-center justify-center font-bold"
            style={{ zIndex: showUrlImage || showLocalImage ? 0 : 1 }}
            aria-hidden
          >
            {fallback.initial}
          </span>
        )}
        {tryUrl && (
          <Image
            src={logoUrl}
            alt=""
            fill
            sizes={`${size}px`}
            className="object-contain p-1.5"
            style={{ zIndex: urlLoaded ? 2 : 0, opacity: urlLoaded ? 1 : 0 }}
            onLoad={() => setUrlLoaded(true)}
            onError={() => setUrlFailed(true)}
            unoptimized={!logoUrl?.startsWith('/')}
          />
        )}
        {tryLocal && (
          <Image
            src={`/images/logos/${slug}.${localExt}`}
            alt=""
            fill
            sizes={`${size * 2}px`}
            className="object-contain p-1.5"
            style={{ zIndex: showLocalImage ? 2 : 0, opacity: showLocalImage ? 1 : 0 }}
            onLoad={() => setLocalLoaded(true)}
            onError={() => {
              if (localExt === 'svg') setLocalExt('png');
              else setLocalFailed(true);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0 ${className}`}
      style={{ width: size, height: size, backgroundColor: '#6b7280' }}
    >
      {siteName.charAt(0)}
    </div>
  );
};
